'use client'

import { Form, FormGroupLabel, FormStore, useFormContext, useFormStore } from '@ariakit/react'
import { ComponentProps, createContext, useRef } from 'react'
import styled, { css } from 'styled-components'

import {
  ColorSwatchWrapper,
  SizeSelectorSelectorButton,
  TypeStyle,
  focusOutline,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import {
  VariantSelectionFormColorOptionFragment,
  VariantSelectionFormFitOptionFragment,
  VariantSelectionFormGroupFieldsFragment,
  VariantSelectionFormSizeOptionFragment,
  VariantSelectionFormVariantFieldsFragment,
} from '../../../__generated__/graphql/catalog/graphql'
import { MakeCartItemProps, useMakeCartItem } from '../../../brunswick/containers/cart/utils'
import {
  WaitlistModalProvider,
  useWaitlistModal,
} from '../../../components/pages/products/ProductPage/WaitlistModal/WaitlistModal'
import { usePagesDirectoryStableQueryParams } from '../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import {
  useCart,
  useCartDialog,
  useCartInterstitialDialog,
} from '../../_providers/CartProvider.client'
import { useFeatureFlag } from '../../_providers/ExperimentationProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { useNextDialogContext } from '../dialogs/NextDialog.client'

export const variantSelectionFormColorOption = gql(`
  fragment VariantSelectionFormColorOption on ProductCatalogProductColorOption {
    color {
      id
      hexCodes
      displayValue
      swatchImage {
        url
      }
      group {
        id
        displayValue
      }
    }
    compareAtPriceRange {
      min {
        amount
      }
      max {
        amount
      }
      minPercentageOff
      maxPercentageOff
    }
    priceRange {
      currencyCode
      min {
        amount
      }
      max {
        amount
      }
    }
    outOfStock
  }
`)

export const variantSelectionFormFitOption = gql(`
  fragment VariantSelectionFormFitOption on ProductCatalogProductFitOption {
    fit {
      id
      displayValue
    }
    outOfStock
  }
`)

export const variantSelectionFormSizeOption = gql(`
  fragment VariantSelectionFormSizeOption on ProductCatalogProductSizeOption {
    size {
      id
      displayValue
    }
    outOfStock
  }
`)

export const variantSelectionFormVariantFields = gql(`
  fragment VariantSelectionFormVariantFields on ProductCatalogVariant {
    __typename
    id
    sku
    availability
    shopifyId
    shopifyProductId
    urlPath
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
  }
`)

export const variantSelectionFormGroupFields = gql(`
  fragment VariantSelectionFormGroupFields on ProductCatalogVariantGroup {
    __typename
    id
    title
    availability
    siteWidePromoEligible
    finalSale
    primaryCategory {
      key
      analyticsId
    }
    color {
      id
      hexCodes
      displayValue
      group {
        id
        displayValue
      }
    }
    variant(fit: $fitFieldValue, size: $sizeFieldValue) {
      ...VariantSelectionFormVariantFields
    }
  }
`)

export const variantSelectionFormProductFields = gql(`
  fragment VariantSelectionFormProductFields on ProductCatalogStandardProduct {
    __typename
    id
    handle
    options(color: $colorFieldValue, fit: $fitFieldValue, size: $sizeFieldValue, collectionHandle: $collectionHandle) {
      colors {
        ...VariantSelectionFormColorOption
      }
      fits {
        ...VariantSelectionFormFitOption
      }
      sizes {
        ...VariantSelectionFormSizeOption
      }
    }
  }
`)

export type VariantSelectionFormContextValue = {
  product: {
    handle: string
    options: {
      colors?: VariantSelectionFormColorOptionFragment[]
      fits?: VariantSelectionFormFitOptionFragment[]
      sizes?: VariantSelectionFormSizeOptionFragment[]
    }
  }
  variantGroup: MakeCartItemProps['variantGroup'] & {
    title: string
    color: VariantSelectionFormGroupFieldsFragment['color']
    availability: VariantSelectionFormGroupFieldsFragment['availability']
    primaryCategory: VariantSelectionFormGroupFieldsFragment['primaryCategory'] &
      MakeCartItemProps['variantGroup']['primaryCategory']
  }
  selectedVariant: VariantSelectionFormSelectedVariant | undefined | null
  loading: boolean
  trackingCategory: string
}

export const VariantSelectionFormContext = createContext<
  VariantSelectionFormContextValue | undefined
>(undefined)

export type VariantSelectionFormState = {
  color: string | undefined | null
  fit: string | undefined | null
  size: string | undefined | null
}

type UseVariantSelectionFormProps<T extends VariantSelectionFormState> = {
  defaultValues: T
}

export const useVariantSelectionForm = <T extends VariantSelectionFormState>({
  defaultValues,
}: UseVariantSelectionFormProps<T>) => {
  const formStore = useFormStore<T>({
    defaultValues,
  })

  const colorFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['color']>('color') ??
    defaultValues.color ??
    ''

  const fitFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['fit']>('fit') ?? ''

  const sizeFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['size']>('size') ?? ''

  return {
    formStore,
    queryVariables: {
      colorFieldValue,
      sizeFieldValue,
      fitFieldValue,
    },
  }
}

export const VariantSelectionFormFields = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${o => o.theme.spacing(6)};
`

export type VariantSelectionFieldProps = {
  onChange?: (newValue: string) => void
}

type VariantSelectionFormSelectedVariant =
  | VariantSelectionFormVariantFieldsFragment
  | undefined
  | null

type VariantSelectionFormBasicState = {
  color: string | undefined | null
  fit: string | undefined | null
  size: string | undefined | null
}

type VariantSelectionFormProps<T extends VariantSelectionFormBasicState> = {
  store: FormStore<T>
} & VariantSelectionFormContextProviderProps &
  Omit<ComponentProps<typeof Form>, 'store'>

export const VariantSelectionForm = <T extends VariantSelectionFormBasicState>({
  loading,
  product,
  variantGroup,
  selectedVariant,
  trackingCategory,
  isPrimaryFormForPageView,
  makeCartItem,
  children,
  ...props
}: VariantSelectionFormProps<T>) => {
  return (
    <Form
      data-testid='variant-picker'
      style={{ display: 'contents' }}
      resetOnSubmit={false}
      {...props}
    >
      <WaitlistModalProvider
        colorDisplayValue={variantGroup.color?.displayValue ?? ''}
        variantShopifyId={selectedVariant?.shopifyId}
        productTitle={variantGroup.title}
      >
        <VariantSelectionFormContextProvider
          isPrimaryFormForPageView={isPrimaryFormForPageView}
          makeCartItem={makeCartItem}
          loading={loading}
          product={product}
          variantGroup={variantGroup}
          selectedVariant={selectedVariant}
          trackingCategory={trackingCategory}
        >
          {children}
        </VariantSelectionFormContextProvider>
      </WaitlistModalProvider>
    </Form>
  )
}

type VariantSelectionFormContextProviderProps = {
  isPrimaryFormForPageView: boolean
  makeCartItem?: ReturnType<typeof useMakeCartItem>
  children?: React.ReactNode
} & VariantSelectionFormContextValue

const VariantSelectionFormContextProvider = ({
  loading: loadingProp,
  product,
  variantGroup,
  selectedVariant,
  trackingCategory,
  isPrimaryFormForPageView,
  makeCartItem: makeCartItemProp,
  children,
}: VariantSelectionFormContextProviderProps) => {
  const defaultMakeCartItem = useMakeCartItem()
  const makeCartItem = makeCartItemProp ?? defaultMakeCartItem
  const dialogContext = useNextDialogContext()
  const { addItems, status: cartStatus } = useCart()
  const formContext = useFormContext()
  const { open: openCartDialog } = useCartDialog()
  const { open: openCartInterstitialDialog } = useCartInterstitialDialog()
  const { openWaitlistModal } = useWaitlistModal()
  const submittingRef = useRef(false)

  /*
  This is a little hacky, but we do this so this button is not shown as loading on
  page load or if a different add to cart is pressed on the same page as this.
  */
  if (cartStatus === 'resolved') {
    submittingRef.current = false
  }
  const loading = loadingProp || (submittingRef.current === true && cartStatus === 'pending')

  const cartInterstitialTestDecision = useFeatureFlag<
    'no-cart-interstitial' | 'yes-cart-interstitial'
  >({
    key: 'cart-interstitial-test',
    controlVariant: 'no-cart-interstitial',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: !isPrimaryFormForPageView,
  })

  // On the PDP we use two submit buttons. So this code cannot exist with the submit component.
  // If you run `useSubmit` in duplicate components aria kit will submit twice for one of the two components.
  formContext?.useSubmit(() => {
    if (selectedVariant) {
      if (selectedVariant.availability === 'AVAILABLE') {
        submittingRef.current = true
        addItems(
          makeCartItem({
            product,
            variant: selectedVariant,
            variantGroup,
            options: {
              attributedPortal: undefined,
            },
          })
        )
        dialogContext?.hide()
        if (cartInterstitialTestDecision === 'yes-cart-interstitial') {
          openCartInterstitialDialog()
        } else {
          openCartDialog()
        }
      } else if (selectedVariant.availability === 'WAIT_LISTING') {
        openWaitlistModal()
      }
    }
  })

  return (
    <VariantSelectionFormContext.Provider
      value={{
        loading,
        product,
        variantGroup,
        selectedVariant,
        trackingCategory,
      }}
    >
      {children}
    </VariantSelectionFormContext.Provider>
  )
}

type VariantSelectionFormQueryParamBindingProps = {
  colorKey: string
  fitKey: string
  sizeKey: string
}

export const VariantSelectionFormPagesDirectoryQueryParamBinding = ({
  colorKey,
  fitKey,
  sizeKey,
}: VariantSelectionFormQueryParamBindingProps) => {
  const { query } = usePagesDirectoryStableQueryParams()
  const formContext = useFormContext()
  const colorQueryRef = useRef(query[colorKey])
  const fitQueryRef = useRef(query[fitKey])
  const sizeQueryRef = useRef(query[sizeKey])

  if (colorQueryRef.current !== query[colorKey]) {
    colorQueryRef.current = query[colorKey]
    formContext!.setValue('color', colorQueryRef.current)
  }

  if (fitQueryRef.current !== query[fitKey]) {
    fitQueryRef.current = query[fitKey]
    formContext!.setValue('fit', fitQueryRef.current)
  }

  if (sizeQueryRef.current !== query[sizeKey]) {
    sizeQueryRef.current = query[sizeKey]
    formContext!.setValue('size', sizeQueryRef.current)
  }

  return null
}

export const VariantSelectionOptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${o => o.theme.spacing(2)};
  width: 100%;
`

export const VariantSelectionOption = styled(SizeSelectorSelectorButton)`
  min-width: 30px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const VariantSelectionInvisibleInput = styled.input`
  height: 0;
  width: 0;
  opacity: 0;

  &:focus-visible + ${ColorSwatchWrapper}, &:focus-visible + ${VariantSelectionOption} {
    ${focusOutline};
    outline-style: solid;
  }
  margin: 0;
  position: absolute;
`

export const variantSelectionFormGroupLabelTypographyCss = css`
  ${TypeStyle.css.headlineMicro};
  font-weight: 700;
  margin-bottom: ${o => o.theme.spacing(2)};
`

export const VariantSelectionFormGroupLabel = styled(FormGroupLabel)`
  ${variantSelectionFormGroupLabelTypographyCss};
  display: flex;
  justify-self: flex-start;
  gap: ${o => o.theme.spacing(1)};
  text-transform: uppercase;
  flex-wrap: wrap;
`

export const VariantSelectionFieldValueDisplay = styled.span`
  color: ${o => o.theme.color.text.tertiary.on.background};
  ${TypeStyle.css.bodyMicro};
  text-transform: none;
`

export const StyledVariantSelectionFromSoldOutMessage = styled.span`
  color: ${o => o.theme.color.stroke.critical.text.on.subtle};
  white-space: nowrap;
`

export const VariantSelectionFromSoldOutMessage = () => {
  const translations = useTranslation()
  const message = translations.detailsSection.soldOutSectionTitleMessage
  return (
    <StyledVariantSelectionFromSoldOutMessage>{message}</StyledVariantSelectionFromSoldOutMessage>
  )
}
